/**
 * Ela: add-to-cart buttons of products that are IN THE CART show
 * "Ga naar afrekenen" (text-only swap, no color change, no checkmark
 * animation) and clicking them navigates to CHECKOUT_URL. The state is driven
 * by the real cart contents: it appears right after an add is confirmed,
 * persists across page loads, and reverts when the product leaves the cart.
 *
 * A NORMAL add-to-cart click is never intercepted or mutated by this script:
 * the theme's AJAX add runs untouched, so adding multiple products (or the
 * same product repeatedly before the state appears) always works.
 *
 * Button-to-cart matching: by product handle via the wishlist heart in the
 * same card (collection/search/cart cards), falling back to the form's
 * variant id (product page, sticky bar, quick-add modal).
 */
import { StandardEvents } from '@shopify/events';

const CHECKOUT_TEXT = 'Ga naar afrekenen';
// Plan is active (Basic) and /checkout resolves to a real checkout session,
// so state-clicks go straight to checkout instead of via the cart page.
const CHECKOUT_URL = '/checkout';

/** @type {Set<string>} product handles currently in the cart */
let cartHandles = new Set();
/** @type {Set<string>} variant ids currently in the cart */
let cartVariantIds = new Set();

// Suppress the theme's checkmark burst: product-form.js sets [data-added] on
// click, which normally fades the label out (atc-fade-out) and fades the
// checkmark in. Keep the label as-is and never show the success icon.
const style = document.createElement('style');
style.textContent = `
  add-to-cart-component button[data-added='true'] .add-to-cart-text {
    animation: none !important;
    opacity: 1 !important;
    filter: none !important;
    translate: none !important;
    transform: none !important;
  }
  add-to-cart-component button .add-to-cart__added,
  add-to-cart-component button .add-to-cart-text--added {
    display: none !important;
  }
`;
document.head.appendChild(style);

/**
 * @param {HTMLButtonElement} button
 * @returns {HTMLElement | null} the innermost span that holds the label text
 */
function getLabel(button) {
  return button.querySelector('.add-to-cart-text__content span span');
}

/** @param {HTMLButtonElement} button */
function isInCart(button) {
  const heart = button.closest('.card-gallery')?.querySelector('.ela-wishlist-heart');
  const handle = heart instanceof HTMLElement ? heart.dataset.handle : null;
  if (handle && cartHandles.has(handle)) return true;

  const variantInput = button.closest('form')?.querySelector('input[name="id"]');
  const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : null;
  return Boolean(variantId && cartVariantIds.has(String(variantId)));
}

/** @param {HTMLButtonElement} button */
function applyState(button) {
  if (button.classList.contains('ela-atc-checkout')) return;
  const label = getLabel(button);
  if (!label) return;

  button.dataset.elaOrigText = label.textContent || '';
  label.textContent = CHECKOUT_TEXT;
  button.classList.add('ela-atc-checkout');
}

/** @param {HTMLButtonElement} button */
function revertState(button) {
  if (!button.classList.contains('ela-atc-checkout')) return;
  button.classList.remove('ela-atc-checkout');

  const label = getLabel(button);
  if (label && button.dataset.elaOrigText) {
    label.textContent = button.dataset.elaOrigText;
  }
}

/** Applies or reverts the state on every add-to-cart button on the page. */
function syncButtons() {
  document.querySelectorAll('add-to-cart-component button[ref="addToCartButton"]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (isInCart(button)) {
      applyState(button);
    } else {
      revertState(button);
    }
  });
}

/** Refetches the cart and re-syncs all buttons. */
async function refreshCart() {
  try {
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const response = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return;
    const cart = await response.json();
    cartHandles = new Set(cart.items.map((/** @type {any} */ item) => item.handle));
    cartVariantIds = new Set(cart.items.map((/** @type {any} */ item) => String(item.id)));
    syncButtons();
  } catch (_) {
    // network hiccup: keep the last known state
  }
}

// Capture phase so the checkout-state click never reaches the theme's
// delegated submit handling (same pattern as ela-wishlist.js). Buttons NOT in
// the checkout state are ignored completely - the theme's add flow runs
// untouched.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('add-to-cart-component button[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    if (button.classList.contains('ela-atc-checkout')) {
      event.preventDefault();
      event.stopPropagation();
      window.location.href = CHECKOUT_URL;
    }
  },
  true
);

// Any cart change (add, remove, quantity, from any component) re-syncs the
// buttons from the real cart, so the state appears right after an add is
// confirmed and disappears exactly when the product leaves the cart.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.promise) {
    event.promise.then(refreshCart, refreshCart);
  } else {
    refreshCart();
  }
});

// Cards injected later (load-more, recommendations) get synced too.
let syncQueued = false;
new MutationObserver(() => {
  if (syncQueued) return;
  syncQueued = true;
  setTimeout(() => {
    syncQueued = false;
    syncButtons();
  }, 100);
}).observe(document.body, { childList: true, subtree: true });

// Initial state + back/forward cache restores.
refreshCart();
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCart();
});
